import {useEffect, useMemo, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {IntelReference} from "../../../../sdo/IntelReference.ts";
import IntelReferenceCard from "../../intel/panels/resources/IntelReferenceCard.tsx";
import IntelReferenceDetail from "../../intel/panels/resources/IntelReferenceDetail.tsx";

// TODO: Replace with API call to AResourceProfileManager.getIntelReferences(typeKey, defKey, matchHash)
function buildTestData(): IntelReference[] {
    return [
        {
            uid: "1",
            display: "Subject A",
            typeKey: "user_profile",
            definitionKey: "individual",
            matchHash: "aa11bb22",
            summary: "Named individual identified in section 4 of this document. Cross-referenced against the active watchlist with a high-confidence match.",
            frames: [
                {
                    uid: "f1",
                    frameNumber: 1,
                    page: 4, section: 2, paragraph: 1, sentence: 3,
                    originalText: "Subject A was reported as present at the gathering described in this section.",
                    pointers: [
                        {uid: "p1", typeKey: "user_profile", display: "Subject A"},
                    ],
                },
                {
                    uid: "f2",
                    frameNumber: 2,
                    page: 6, section: 1, paragraph: 2, sentence: 1,
                    originalText: "A second mention places Subject A at the same location two days later.",
                    pointers: [
                        {uid: "p2", typeKey: "user_profile", display: "Subject A"},
                    ],
                },
            ],
            metadata: {confidence: "high"},
        },
        {
            uid: "2",
            display: "Acme Holdings LLC",
            typeKey: "organization",
            definitionKey: "corporation",
            matchHash: "cc33dd44",
            summary: "Corporate entity referenced in the legal disclosures section. Linked to several individuals in the existing intel base.",
            frames: [
                {
                    uid: "f3",
                    frameNumber: 1,
                    page: 12, section: 3, paragraph: 4, sentence: 2,
                    originalText: "Acme Holdings LLC is identified as the holding entity for the assets in question.",
                    pointers: [],
                },
            ],
            metadata: {jurisdiction: "DE-US"},
        },
        {
            uid: "3",
            display: "Operation Nightfall",
            typeKey: "campaign",
            definitionKey: "named_operation",
            matchHash: "55ee66ff",
            summary: "",
            frames: [],
            metadata: {phase: "active"},
        },
        {
            uid: "4",
            display: "192.168.4.0/24",
            typeKey: "network_address",
            definitionKey: "ipv4_cidr",
            matchHash: "77aa88bb",
            summary: "Network range mentioned in the appendix as the source of the suspicious DNS lookups.",
            frames: [
                {
                    uid: "f4",
                    frameNumber: 1,
                    page: 18, section: 4, paragraph: 1, sentence: 5,
                    originalText: "Traffic originating from 192.168.4.0/24 was correlated with the timeline of events.",
                    pointers: [],
                },
            ],
            metadata: {},
        },
    ];
}

function UXResourceIntelRefPanel() {
    const [references] = useState<IntelReference[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [selectedUid, setSelectedUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return references;
        const q = filterText.toLowerCase();
        return references.filter((r) =>
            r.display.toLowerCase().includes(q)
            || r.typeKey.toLowerCase().includes(q)
            || r.definitionKey.toLowerCase().includes(q)
            || (r.summary && r.summary.toLowerCase().includes(q))
        );
    }, [references, filterText]);

    // Standard split-pane behavior: ensure selectedUid is always one of the
    // currently-visible items, auto-selecting the first when needed.
    useEffect(() => {
        if (filtered.length === 0) {
            if (selectedUid !== null) setSelectedUid(null);
            return;
        }
        const stillVisible = filtered.some((r) => r.uid === selectedUid);
        if (!stillVisible) {
            setSelectedUid(filtered[0].uid);
        }
    }, [filtered, selectedUid]);

    const selected = useMemo(() => {
        if (!selectedUid) return null;
        return filtered.find((r) => r.uid === selectedUid) || null;
    }, [filtered, selectedUid]);

    const handleSelect = (reference: IntelReference) => {
        setSelectedUid(reference.uid);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Intelligence References</Typography>
            </Stack>

            {/* Split layout: card list (left 1/3) + detail pane (right 2/3, hidden if no items) */}
            <Stack direction="row" spacing={0} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {/* Left: label + filter + card list — fixed-width 300px cards that flow 1-2 per row */}
                <Box sx={{
                    flex: selected ? "0 0 33.333%" : "1 1 100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    paddingRight: selected ? "12px" : 0,
                    borderRight: selected ? "1px solid #3F464F" : "none",
                }}>
                    <Box sx={{borderBottom: "1px solid #3F464F", paddingBottom: "6px", marginBottom: "10px"}}>
                        <Typography sx={{
                            color: "#a8b8c0",
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            textAlign: "left",
                        }}>
                            Select a Reference Item
                        </Typography>
                    </Box>
                    {/* Filter bar — scoped to the reference item list */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{marginBottom: "10px", flexWrap: "wrap"}}>
                        <StyledSearch>
                            <StyledSearchIconWrapper>
                                <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                            </StyledSearchIconWrapper>
                            <StyledInputBase
                                placeholder="Filter reference items..."
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                        </StyledSearch>
                        <Typography sx={appStyles.panelFilterCount}>
                            Showing {filtered.length} of {references.length}
                        </Typography>
                    </Stack>
                    {filtered.length === 0 ? (
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.4)",
                            fontSize: "0.80rem",
                            textAlign: "center",
                            padding: "24px",
                        }}>
                            No items have been found
                        </Typography>
                    ) : (
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "flex-start"}}>
                            {filtered.map((ref) => (
                                <IntelReferenceCard
                                    key={ref.uid}
                                    reference={ref}
                                    selected={selectedUid === ref.uid}
                                    onClick={handleSelect}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Right: detail pane (only when a card is selected) */}
                {selected && (
                    <Box sx={{
                        flex: "1 1 66.666%",
                        minWidth: 300,
                        display: "flex",
                        flexDirection: "column",
                        paddingLeft: "12px",
                    }}>
                        <Box sx={{borderBottom: "1px solid #3F464F", paddingBottom: "6px", marginBottom: "10px"}}>
                            <Typography sx={{
                                color: "#a8b8c0",
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                letterSpacing: 0.3,
                                textAlign: "left",
                            }}>
                                Details for Selected Reference
                            </Typography>
                        </Box>
                        <Box sx={{flex: 1, minHeight: 0}}>
                            <IntelReferenceDetail reference={selected}/>
                        </Box>
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}

export default UXResourceIntelRefPanel;
